// DISPLAY prominence for source lists: the most-studied meforshim lead their
// section (Rashi before Abarbanel), instead of the accident of the alphabet.
// This is a UX ORDERING choice only - it never invents a source, a label or a
// relationship (accuracy-lock untouched), and it never crosses sections: the
// taxonomy still decides WHERE a source appears (Onkelos is a Targum, so he
// leads the Targum section - never "Classic commentaries"; Rambam wrote no
// Torah commentary, so his Mishneh Torah / Moreh citations surface under
// Halakhah / Jewish Thought, never as a chumash mefaresh - Rambam != Ramban).
// Unlisted names keep their alphabetical order AFTER the listed ones.

import { norm } from "./taxonomy.ts";

// One global famous-first list. Sections partition independently (era.ts /
// category), so ranks only ever compete WITHIN a section; interleaving tiers
// here is harmless and keeps one source of truth.
const ORDER: string[] = [
  // classic commentaries (rishonim), in study-prominence order
  "rashi",
  "ramban",
  "ibn ezra",
  "rashbam",
  "sforno",
  "chizkuni",
  "radak",
  "baal haturim",
  "kitzur baal haturim",
  "daat zekenim",
  "rabbeinu bahya",
  "abarbanel",
  "ralbag",
  "saadia gaon",
  // later commentaries (acharonim)
  "or hachaim",
  "kli yakar",
  "siftei chakhamim",
  "malbim",
  "haamek davar",
  "harchev davar",
  "rav hirsch",
  "metzudat david",
  "metzudat zion",
  "torah temimah",
  "meshech hochma",
  "gur aryeh",
  "shadal",
  // targumim
  "onkelos",
  "targum jonathan",
  "targum yerushalmi",
  "tafsir rasag",
];

const RANK: ReadonlyMap<string, number> = new Map(ORDER.map((n, i) => [n, i]));

export const UNRANKED = Number.MAX_SAFE_INTEGER;

// Rank for sorting (lower = more prominent; UNRANKED = alphabetical tail).
// Matches the same two keys era.ts/taxonomy use - the normalized collective
// title, then the "X" head of an "X on Book" ref - plus a prefix form for
// per-book collective titles (Sefaria's Onkelos links carry "Onkelos Numbers",
// verified live 2026-06-10). Prefix requires a following space so a key can
// never swallow an unrelated longer name.
export function prominenceRank(commentator: string, ref?: string): number {
  const name = norm(commentator);
  const direct = RANK.get(name);
  if (direct !== undefined) return direct;
  if (ref && / on /i.test(ref)) {
    const head = norm(ref.split(/ on /i)[0]);
    const byHead = RANK.get(head);
    if (byHead !== undefined) return byHead;
  }
  for (const [key, rank] of RANK) {
    if (name.startsWith(key + " ")) return rank;
  }
  return UNRANKED;
}
